import type { ColumnDef } from '@tanstack/react-table';
import type { User } from '~/common/types';
import { compareCreatedAt, formatCreatedAt, matchesOrganization, organizationLabel } from './columnHelpers';

export const columns: ColumnDef<User>[] = [
  {
    id: 'email',
    header: 'Email',
    accessorKey: 'email',
    // Explicit width rather than AUTO: this table has seven columns in a ~2/3-width pane, so the fixed
    // columns already exceed the available space and the table scrolls horizontally. An AUTO column in
    // that overflow case never gets redistributed and stays at its ~1000px sentinel, ballooning email
    // and pushing every other column off-screen. A fixed width keeps email readable and the rest visible.
    size: 320,
  },
  {
    id: 'firstName',
    header: 'First Name',
    accessorKey: 'firstName',
  },
  {
    id: 'lastName',
    header: 'Last Name',
    accessorKey: 'lastName',
  },
  {
    id: 'organization',
    header: 'Organization',
    size: 220,
    accessorFn: organizationLabel,
    filterFn: (row, _columnId, value: string) => matchesOrganization(row.original, value),
    cell: ({ getValue }) => <div data-testid="unapproved-users-org-cell">{getValue() as string}</div>,
  },
  {
    id: 'completedPersona',
    header: 'Completed Persona',
    accessorFn: (row) => {
      return row.verificationApprovalWebhookReceived?.[0]?.inquiryId;
    },
    cell: ({ getValue }) => {
      return getValue() ? '✅' : '❌';
    },
  },
  {
    id: 'isApproved',
    header: 'Approved',
    accessorKey: 'isApproved',
    cell: ({ getValue }) => <div className="text-center">{getValue() ? '✅' : '❌'}</div>,
  },
  {
    id: 'createdAt',
    header: 'Created At',
    size: 140,
    accessorFn: formatCreatedAt,
    sortingFn: (a, b) => compareCreatedAt(a.original, b.original),
    cell: ({ getValue }) => <div data-testid="unapproved-users-created-at-cell">{getValue() as string}</div>,
  },
];
